import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Trash2, Layers, HelpCircle, Download } from 'lucide-react';
import { PricingModel, Variable } from '@/features/pricing-models/types/model.types';
import { Material } from '@/features/materials/types/material.types';
import { VariableLink, UpstreamMaterialPrice } from '../types/quote.types';
import { GlobalVariablesCard, AvailableSource } from './global-variables-card';
import { LineItemsTable, LineItemRow } from './line-items-table';
import { modelService } from '@/features/pricing-models/services/model-service';
import { masterDataService } from '@/features/master-data/services/master-data-service';
import { formatNumber } from '@/lib/utils/format';
import type { QuoteShipment } from '../types/quote-shipment.types';

export interface SectionState {
  id: string;
  modelId: string;
  order: number;
  globalInputs: Record<string, any>;
  variableLinks: Record<string, VariableLink>;
  lineItems: LineItemRow[];
  variables: Variable[];
}

interface QuoteSectionCardProps {
  sectionIndex: number;
  models: PricingModel[];
  materials: Material[];
  section: SectionState;
  allSections: SectionState[];
  onUpdate: (section: SectionState) => void;
  onRemove: () => void;
  currencySymbol?: string;
  canRemove?: boolean;
  upstreamPrices?: Record<string, UpstreamMaterialPrice>;
  upstreamVariableCode?: string;
  upstreamTotalVariableCode?: string;
  freightByLineItemId?: Record<string, number>;
  shipments?: QuoteShipment[];
  onShipmentChange?: (lineItemId: string, shipmentId: string) => Promise<void> | void;
  onCreateShipment?: () => Promise<QuoteShipment | null>;
  onEnsureDefaultShipment?: () => Promise<QuoteShipment | null>;
  /** Secciones de la cotización origen (para elegir de dónde cargar los productos). */
  sourceQuoteSections?: Array<{
    sectionId: string;
    sectionLabel: string;
    items: Array<{
      materialId: string;
      quantity: number;
      unitPrice: number;
      totalPrice: number;
    }>;
  }>;
  /** Agregado global (todas las secciones combinadas con promedio ponderado). */
  sourceQuoteAllItems?: Array<{
    materialId: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
  }>;
  sourceQuoteCode?: string;
}

export function QuoteSectionCard({
  sectionIndex,
  models,
  materials,
  section,
  allSections,
  onUpdate,
  onRemove,
  currencySymbol = '$',
  canRemove = true,
  upstreamPrices,
  upstreamVariableCode,
  upstreamTotalVariableCode,
  freightByLineItemId,
  shipments,
  onShipmentChange,
  onCreateShipment,
  onEnsureDefaultShipment,
  sourceQuoteSections = [],
  sourceQuoteAllItems = [],
  sourceQuoteCode,
}: QuoteSectionCardProps) {
  const [loadFromSectionId, setLoadFromSectionId] = useState<string>('all');
  const [loadingVars, setLoadingVars] = useState(false);

  const selectedModel = models.find((m) => m.id === section.modelId);

  // Load variables when model changes
  useEffect(() => {
    if (!section.modelId) return;

    // If the model already has variables loaded in the models list, use those
    const model = models.find((m) => m.id === section.modelId);
    if (model && model.variables && model.variables.length > 0) {
      onUpdate({ ...section, variables: model.variables });
      return;
    }

    // Otherwise fetch from API
    setLoadingVars(true);
    modelService
      .getVariables(section.modelId)
      .then((vars) => {
        onUpdate({ ...section, variables: vars });
      })
      .catch((err) => console.error('Error loading variables:', err))
      .finally(() => setLoadingVars(false));
  }, [section.modelId]);

  // Auto-fill default values for Global Variables when variables load.
  // Sin esto, los inputs muestran el defaultValue como placeholder gris pero el
  // state arranca vacío y al guardar la cotización los valores van como null/0.
  // Solo seteamos si el valor todavía no fue editado por el usuario.
  useEffect(() => {
    if (section.variables.length === 0) return;

    const globalVars = section.variables.filter((v) => v.isGlobal && !v.formula);
    if (globalVars.length === 0) return;

    const next: Record<string, any> = { ...section.globalInputs };
    let changed = false;
    for (const v of globalVars) {
      const code = v.code;
      if (!code) continue;
      // No pisar valores que el usuario ya editó (incluye 0).
      if (next[code] != null && next[code] !== '') continue;
      const dv = (v as any).defaultValue;
      if (dv == null || dv === '') continue;
      const num =
        v.variableType === 'TEXT' ? NaN : typeof dv === 'number' ? dv : parseFloat(String(dv));
      if (Number.isFinite(num)) {
        next[code] = num;
        changed = true;
      } else {
        next[code] = dv;
        changed = true;
      }
    }
    if (changed) {
      onUpdate({ ...section, globalInputs: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.variables.length, section.modelId]);

  // Auto-fill TIPO_CAMBIO when variables load
  useEffect(() => {
    if (section.variables.length === 0) return;

    const tipoCambioVar = section.variables.find((v) => v.code === 'TIPO_CAMBIO' && v.isGlobal);
    if (!tipoCambioVar) return;
    if (section.globalInputs['TIPO_CAMBIO']) return;

    const autoFill = async () => {
      const rate =
        (await masterDataService.getCurrentExchangeRate('USDT')) ??
        (await masterDataService.getCurrentExchangeRate('USDC'));
      if (rate) {
        onUpdate({
          ...section,
          globalInputs: { ...section.globalInputs, TIPO_CAMBIO: rate },
        });
      }
    };
    autoFill().catch(() => {});
  }, [section.variables.length, section.modelId]);

  const handleModelChange = (modelId: string) => {
    onUpdate({
      ...section,
      modelId,
      variables: [],
      globalInputs: {},
      variableLinks: {},
      lineItems: [],
    });
  };

  const handleGlobalInputsChange = useCallback(
    (globalInputs: Record<string, any>) => {
      onUpdate({ ...section, globalInputs });
    },
    [section, onUpdate]
  );

  const handleLinkChange = useCallback(
    (links: Record<string, VariableLink>) => {
      // When links change, also resolve values from source items
      const newGlobalInputs = { ...section.globalInputs };
      Object.entries(links).forEach(([varCode, link]) => {
        const sourceSection = allSections.find((s) => s.id === link.sourceSectionId);
        const sourceItem = sourceSection?.lineItems.find((li) => li.id === link.sourceItemId);
        if (sourceItem) {
          newGlobalInputs[varCode] = sourceItem.totalPrice;
        }
      });
      onUpdate({ ...section, variableLinks: links, globalInputs: newGlobalInputs });
    },
    [section, allSections, onUpdate]
  );

  const handleLineItemsChange = useCallback(
    (lineItems: LineItemRow[]) => {
      onUpdate({ ...section, lineItems });
    },
    [section, onUpdate]
  );

  // Items actualmente seleccionados según el filtro de sección de origen
  const activeSourceItems = useMemo(() => {
    if (loadFromSectionId === 'all') return sourceQuoteAllItems;
    const sec = sourceQuoteSections.find((s) => s.sectionId === loadFromSectionId);
    return sec?.items ?? [];
  }, [loadFromSectionId, sourceQuoteSections, sourceQuoteAllItems]);

  // Items de la cotización origen (filtrados por sección elegida) que todavía no están en la sección downstream
  const pendingSourceItems = useMemo(() => {
    const existingMaterialIds = new Set(
      section.lineItems.map((li) => li.materialId).filter(Boolean)
    );
    return activeSourceItems.filter(
      (it) => it.materialId && !existingMaterialIds.has(it.materialId)
    );
  }, [activeSourceItems, section.lineItems]);

  // Priorización en combobox: todos los materiales del origen (todas las secciones)
  const sourceMaterialIds = useMemo(
    () => sourceQuoteAllItems.map((it) => it.materialId).filter(Boolean),
    [sourceQuoteAllItems]
  );

  const handleLoadSourceQuoteItems = useCallback(() => {
    if (pendingSourceItems.length === 0) return;
    // Defaults del modelo actual (solo variables PER_LINE editables y no calculadas).
    const modelDefaults: Record<string, any> = {};
    for (const v of section.variables) {
      if ((v as any).isGlobal) continue;
      if (v.variableType === 'CALCULATED') continue;
      if (v.formula) continue;
      if (v.code === 'CANTIDAD') continue;
      const dv = (v as any).defaultValue;
      if (dv != null && dv !== '') {
        modelDefaults[v.code] = dv;
      }
    }
    const newRows: LineItemRow[] = pendingSourceItems.map((it, idx) => {
      // Arrancamos con los defaults del modelo y luego sobrescribimos con los valores del origen.
      const costInputs: Record<string, any> = { ...modelDefaults };
      // Pre-poblar variable upstream unitaria (UNIT_PRICE del origen)
      if (upstreamVariableCode) {
        costInputs[upstreamVariableCode] = it.unitPrice;
      }
      // Pre-poblar variable upstream total (TOTAL_PRICE del origen, p.ej. "COSTO_ALMACEN")
      if (upstreamTotalVariableCode) {
        costInputs[upstreamTotalVariableCode] = it.totalPrice;
      }
      return {
        id: `source-${Date.now()}-${idx}`,
        materialId: it.materialId,
        quantity: it.quantity,
        costInputs,
        unitPrice: it.unitPrice,
        totalPrice: it.totalPrice,
        calculations: {},
        shipmentId: null,
      };
    });
    onUpdate({ ...section, lineItems: [...section.lineItems, ...newRows] });
  }, [pendingSourceItems, section, onUpdate, upstreamVariableCode, upstreamTotalVariableCode]);

  // Build available sources from other sections (exclude self)
  const availableSources: AvailableSource[] = useMemo(() => {
    return allSections
      .filter((s) => s.id !== section.id && s.lineItems.length > 0)
      .map((s) => {
        const model = models.find((m) => m.id === s.modelId);
        const sectionIdx = allSections.findIndex((as) => as.id === s.id);
        return {
          sectionId: s.id,
          sectionLabel: `Sección ${sectionIdx + 1}${model ? ` — ${model.name}` : ''}`,
          items: s.lineItems.map((li) => {
            const mat = materials.find((m) => m.id === li.materialId);
            return {
              itemId: li.id,
              itemLabel: mat ? `${mat.code} - ${mat.name}` : `Item ${li.id.slice(0, 6)}`,
              totalPrice: li.totalPrice,
            };
          }),
        };
      });
  }, [allSections, section.id, models, materials]);

  // Separate global vs per-item variables
  const globalVariables = section.variables.filter((v) => v.isGlobal && !v.formula);
  const perItemVariables = section.variables.filter((v) => !v.isGlobal);

  // Section subtotal
  const sectionTotal = section.lineItems.reduce((sum, item) => sum + item.totalPrice, 0);

  return (
    <Card className="border-2 border-dashed">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Layers className="w-5 h-5" />
            Sección {sectionIndex + 1}
            {selectedModel && (
              <span className="text-muted-foreground font-normal">— {selectedModel.name}</span>
            )}
          </CardTitle>
          <div className="flex items-center gap-3">
            {sectionTotal > 0 && (
              <span className="text-sm font-semibold bg-alerta-suave text-foreground px-3 py-1 rounded-full">
                Subtotal: {currencySymbol} {formatNumber(sectionTotal)}
              </span>
            )}
            {canRemove && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onRemove}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Eliminar
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Eliminar esta sección y todos sus items de línea</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Model selector */}
        <div data-tour="create-model" className="max-w-sm">
          <div className="flex items-center gap-1.5 mb-1">
            <Label>Modelo de Pricing *</Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                Selecciona el modelo de pricing que define las variables y formulas de calculo para
                esta sección. Cada sección puede usar un modelo diferente.
              </TooltipContent>
            </Tooltip>
          </div>
          <SearchableSelect
            options={models
              .filter((m) => m.isActive)
              .map((m) => ({ value: m.id, label: m.code ? `${m.name} (${m.code})` : m.name }))}
            value={section.modelId || undefined}
            onValueChange={handleModelChange}
            placeholder="Selecciona un modelo"
            searchPlaceholder="Buscar modelo..."
          />
        </div>

        {loadingVars && (
          <p className="text-sm text-muted-foreground">Cargando variables del modelo...</p>
        )}

        {/* Global Variables */}
        {section.modelId && globalVariables.length > 0 && (
          <div data-tour="create-variables">
            <GlobalVariablesCard
              variables={globalVariables}
              values={section.globalInputs}
              onChange={handleGlobalInputsChange}
              variableLinks={section.variableLinks}
              onLinkChange={handleLinkChange}
              availableSources={availableSources}
            />
          </div>
        )}

        {/* Line Items Table */}
        {section.modelId && section.variables.length > 0 && (
          <div data-tour="create-items" className="space-y-2">
            {sourceQuoteAllItems.length > 0 && (
              <div className="rounded-md border border-info-borde bg-info-suave px-3 py-2 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-xs text-foreground">
                    Cotización origen
                    {sourceQuoteCode ? ` ${sourceQuoteCode}` : ''}
                    {sourceQuoteSections.length > 1 && (
                      <>
                        {' '}
                        — tiene <strong>{sourceQuoteSections.length}</strong> secciones
                      </>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    {sourceQuoteSections.length > 1 && (
                      <Select value={loadFromSectionId} onValueChange={setLoadFromSectionId}>
                        <SelectTrigger className="h-7 w-[220px] bg-white text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            Todas ({sourceQuoteAllItems.length} items, promedio ponderado)
                          </SelectItem>
                          {sourceQuoteSections.map((s) => (
                            <SelectItem key={s.sectionId} value={s.sectionId}>
                              {s.sectionLabel} ({s.items.length})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 bg-white"
                      onClick={handleLoadSourceQuoteItems}
                      disabled={pendingSourceItems.length === 0}
                    >
                      <Download className="w-3 h-3 mr-1" />
                      {pendingSourceItems.length > 0
                        ? `Cargar ${pendingSourceItems.length} producto(s)`
                        : 'Todos ya cargados'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <LineItemsTable
              allVariables={section.variables}
              perItemVariables={perItemVariables}
              globalVariables={globalVariables}
              lookupTables={selectedModel?.lookupTables ?? []}
              globalInputs={section.globalInputs}
              materials={materials}
              lineItems={section.lineItems}
              onChange={handleLineItemsChange}
              currencySymbol={currencySymbol}
              upstreamPrices={upstreamPrices}
              upstreamVariableCode={upstreamVariableCode}
              freightByLineItemId={freightByLineItemId}
              shipments={shipments}
              onShipmentChange={onShipmentChange}
              onCreateShipment={onCreateShipment}
              onEnsureDefaultShipment={onEnsureDefaultShipment}
              priorityMaterialIds={sourceMaterialIds}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default QuoteSectionCard;
